/**
 * Viewport subscription hook with debounced bbox updates.
 */

import { useCallback, useRef, useEffect, useMemo } from 'react';
import { debounce } from 'lodash-es';
import { websocketService } from '../services/websocket';
import { useConnectionStore } from '../stores/connectionStore';
import { useFilterStore } from '../stores/filterStore';
import { VIEWPORT_CONFIG } from '../config';

export interface ViewportBounds {
  west: number;   // minLon
  south: number;  // minLat
  east: number;   // maxLon
  north: number;  // maxLat
}

export interface UseViewportOptions {
  /** Debounce delay in ms (default from config) */
  debounceMs?: number;
  /** Auto-subscribe when connected (default: true) */
  autoSubscribe?: boolean;
}

export interface UseViewportReturn {
  /** Update viewport bounds (triggers debounced subscription) */
  updateBounds: (bounds: ViewportBounds) => void;
  /** Update zoom level */
  updateZoom: (zoom: number) => void;
  /** Current subscribed bbox */
  currentBbox: [number, number, number, number] | null;
  /** Force immediate subscription (bypasses debounce) */
  forceSubscribe: (bounds: ViewportBounds) => void;
}

/**
 * Hook for managing viewport subscriptions with debouncing.
 */
export function useViewport(options: UseViewportOptions = {}): UseViewportReturn {
  const { debounceMs = VIEWPORT_CONFIG.DEBOUNCE_MS, autoSubscribe = true } = options;

  const currentBbox = useConnectionStore((state) => state.currentViewport);
  const status = useConnectionStore((state) => state.status);
  const setZoomLevel = useFilterStore((state) => state.setZoomLevel);

  const lastBoundsRef = useRef<ViewportBounds | null>(null);

  // Subscribe to viewport
  const subscribe = useCallback((bounds: ViewportBounds) => {
    const bbox: [number, number, number, number] = [
      bounds.west,
      bounds.south,
      bounds.east,
      bounds.north,
    ];

    websocketService.subscribeViewport(bbox);
  }, []);

  // Create debounced subscription function using useMemo (stable across renders)
  // NOTE: Do not gate on connection status here — websocketService.subscribeViewport
  // stores the bbox in the connection store and websocketService.send() queues
  // messages if not yet connected. Gating here causes a silent drop when the map
  // loads before the WebSocket connects.
  const debouncedSubscribe = useMemo(
    () =>
      debounce((bounds: ViewportBounds) => {
        subscribe(bounds);
      }, debounceMs),
    [debounceMs, subscribe]
  );

  // Re-subscribe when connection is established
  useEffect(() => {
    if (status === 'connected' && autoSubscribe) {
      const bounds = lastBoundsRef.current;
      if (bounds) {
        subscribe(bounds);
      }
    }
  }, [status, autoSubscribe, subscribe]);

  // Cleanup debounce on unmount
  useEffect(() => {
    const fn = debouncedSubscribe;
    return () => {
      fn.cancel();
    };
  }, [debouncedSubscribe]);

  const updateBounds = useCallback(
    (bounds: ViewportBounds) => {
      lastBoundsRef.current = bounds;
      debouncedSubscribe(bounds);
    },
    [debouncedSubscribe]
  );

  const updateZoom = useCallback(
    (zoom: number) => {
      setZoomLevel(zoom);
    },
    [setZoomLevel]
  );

  const forceSubscribe = useCallback(
    (bounds: ViewportBounds) => {
      debouncedSubscribe.cancel();
      lastBoundsRef.current = bounds;
      // Always call subscribe — websocketService.send() queues if not connected,
      // and subscribeViewport stores the bbox for re-subscribe on reconnect.
      subscribe(bounds);
    },
    [debouncedSubscribe, subscribe]
  );

  return {
    updateBounds,
    updateZoom,
    currentBbox,
    forceSubscribe,
  };
}
